import React, { useState, useEffect } from "react";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import Chip from "@mui/material/Chip";

import { useAtom } from "jotai";
import { labelColoursAtom } from "../-Atoms";

// Firebase imports
import { db, updateDoc, doc } from "../firebaseConfig";

const { DateTime } = require("luxon");

const DueDateChip = ({
  dueDate,
  complete,
  expressDueDate,
  expressComplete,
  id,
  theme,
  canEdit,
  flexAlign,
  cardBackgroundColour,
}) => {
  const [labelColours] = useAtom(labelColoursAtom);
  const [shouldShowNormal, setShouldShowNormal] = useState(true);

  const same = DateTime.fromISO(expressDueDate)
    .setZone("Australia/Sydney")
    .startOf("day")
    .equals(
      DateTime.fromISO(dueDate).setZone("Australia/Sydney").startOf("day")
    );

  useEffect(() => {
    if (expressDueDate) {
      setShouldShowNormal(!same);
    }
  }, [expressDueDate, dueDate, same]);

  return (
    <div
      style={{ 
        display: "flex", 
        flexDirection: "column",
        alignItems: flexAlign ? flexAlign : "flex-start",
        gap: "8px"
      }}
    >
      {shouldShowNormal ? (
        <TheChip
          dueDate={dueDate}
          complete={complete}
          labelColours={labelColours}
          theme={theme}
          id={id}
          canEdit={canEdit}
          express={false}
          expressDueDate={expressDueDate}
          dueDateActual={dueDate}
          cardBackgroundColour={cardBackgroundColour}
        />
      ) : null}
      {expressDueDate ? (
        <TheChip
          dueDate={expressDueDate}
          complete={expressComplete}
          labelColours={labelColours}
          theme={theme}
          id={id}
          canEdit={canEdit}
          express={true}
          expressDueDate={expressDueDate}
          dueDateActual={dueDate}
          cardBackgroundColour={cardBackgroundColour}
        />
      ) : null}
    </div>
  );
};

export default DueDateChip;

const TheChip = ({
  dueDate,
  complete,
  labelColours,
  theme,
  id,
  canEdit,
  express,
  expressDueDate,
  dueDateActual,
  cardBackgroundColour,
}) => {
  const [DueDateIcon, setDueDateIcon] = useState(
    complete ? <CheckBoxIcon /> : <AccessTimeIcon />
  );

  const changeIcon = (complete) => {
    if (complete) {
      setDueDateIcon(<CheckBoxIcon />);
    } else {
      setDueDateIcon(<CheckBoxOutlineBlankIcon />);
    }
  };

  const same = DateTime.fromISO(expressDueDate)
    .setZone("Australia/Sydney")
    .startOf("day")
    .equals(
      DateTime.fromISO(dueDateActual).setZone("Australia/Sydney").startOf("day")
    );

  return (
    <Chip
      icon={DueDateIcon}
      label={DateTime.fromISO(dueDate)
        .setZone("Australia/Sydney")
        .toFormat("dd LLL")}
      sx={{
        cursor: canEdit ? "pointer" : "default",
        backgroundColor: getDueDateColour(
          dueDate,
          complete,
          theme,
          labelColours,
          express,
          cardBackgroundColour
        ),
        height: 32,
        borderRadius: 2,
        fontSize: "0.75rem",
        fontWeight: 600,
        border: express ? `2px solid ${labelColours["pink_dark"][theme]}` : "none",
        boxShadow: theme === "dark" 
          ? "0 2px 8px rgba(0,0,0,0.3)" 
          : "0 2px 8px rgba(0,0,0,0.15)",
        transition: "all 0.2s ease-in-out",
        "& .MuiChip-icon": {
          fontSize: "1rem",
          marginLeft: "8px",
        },
        "& .MuiChip-label": {
          paddingLeft: "4px",
          paddingRight: "12px",
          fontSize: "0.75rem",
          fontWeight: 600,
        },
        "&:hover": {
          backgroundColor: canEdit
            ? newShade(
                getDueDateColour(
                  dueDate,
                  complete,
                  theme,
                  labelColours,
                  express,
                  cardBackgroundColour
                ),
                theme === "dark" ? -30 : 30
              )
            : getDueDateColour(
                dueDate,
                complete,
                theme,
                labelColours,
                express,
                cardBackgroundColour
              ),
          transform: canEdit ? "translateY(-1px)" : "none",
          boxShadow: canEdit 
            ? (theme === "dark" 
                ? "0 4px 12px rgba(0,0,0,0.4)" 
                : "0 4px 12px rgba(0,0,0,0.2)")
            : (theme === "dark" 
                ? "0 2px 8px rgba(0,0,0,0.3)" 
                : "0 2px 8px rgba(0,0,0,0.15)"),
        },
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (canEdit) {
          complete = !complete;
          changeIcon(complete);

          if (express) {
            updateDoc(doc(db, "JobData", id), { expressComplete: complete });

            if (same) {
              updateDoc(doc(db, "JobData", id), { complete: complete });
            }
          } else {
            updateDoc(doc(db, "JobData", id), { complete: complete });
            if (expressDueDate) {
              if (same) {
                updateDoc(doc(db, "JobData", id), { expressComplete: complete });
              }
            }
          }
        }
      }}
      onMouseEnter={() => {
        changeIcon(complete);
      }}
      onMouseLeave={() => {
        setDueDateIcon(complete ? <CheckBoxIcon /> : <AccessTimeIcon />);
      }}
      size="small"
    />
  );
};

const getDueDateColour = (
  dueDate,
  complete,
  theme,
  labelColours,
  express,
  cardBackgroundColour
) => {
  if (complete)
    return express
      ? labelColours["pink_dark"][theme]
      : labelColours["lime"][theme];

  if (
    DateTime.fromISO(dueDate)
      .setZone("Australia/Sydney")
      .startOf("day")
      .equals(DateTime.now().setZone("Australia/Sydney").startOf("day"))
  )
    return labelColours["yellow"][theme];
  if (
    DateTime.fromISO(dueDate).setZone("Australia/Sydney").startOf("day") <=
    DateTime.now().setZone("Australia/Sydney").startOf("day")
  )
    return labelColours["red"][theme];
  // return theme === "dark" ? "#EA3323" : "#f24130";

  return cardBackgroundColour
    ? cardBackgroundColour
    : theme === "dark"
    ? "#272727"
    : "#d6d6d6";
};

const newShade = (hexColor, magnitude) => {
  if (!hexColor || typeof hexColor !== 'string') return '#000000';
  hexColor = hexColor.replace(`#`, ``);
  if (hexColor.length === 6) {
    const decimalColor = parseInt(hexColor, 16);
    let r = (decimalColor >> 16) + magnitude;
    r > 255 && (r = 255);
    r < 0 && (r = 0);
    let g = (decimalColor & 0x0000ff) + magnitude;
    g > 255 && (g = 255);
    g < 0 && (g = 0);
    let b = ((decimalColor >> 8) & 0x00ff) + magnitude;
    b > 255 && (b = 255);
    b < 0 && (b = 0);
    let toReturn = (g | (b << 8) | (r << 16)).toString(16);
    if (toReturn.length === 5) {
      toReturn = `0${toReturn}`;
    }
    return `#${toReturn}`;
  } else {
    return hexColor;
  }
};
